import React, { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Clock, User, Bike, Car, ShieldCheck } from "lucide-react";
import { getVisitorPhotoSrc } from "@/components/VisitorPhotoModal";

function formatActivityTime(item) {
    let label = "Updated";
    let timeVal = item.activityTime || item.entered_at || item.created_at;

    if (item.exited_at) {
        label = "Exited";
        timeVal = item.exited_at;
    } else if (item.status === "entered" && item.entered_at) {
        label = "Entered";
        timeVal = item.entered_at;
    } else if (item.status === "approved" && item.decided_at) {
        label = "Approved";
        timeVal = item.decided_at;
    }

    if (!timeVal) return "";
    try {
        const d = new Date(timeVal);
        if (isNaN(d.getTime())) return "";
        const now = Date.now();
        const diffMs = now - d.getTime();
        const diffMins = Math.floor(diffMs / (1000 * 60));

        let timeStr = "";
        if (diffMins < 1) {
            timeStr = "just now";
        } else if (diffMins < 60) {
            timeStr = `${diffMins}m ago`;
        } else {
            timeStr = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
        }
        return `${label} ${timeStr}`;
    } catch {
        return "";
    }
}

export default function ResidentRecentActivity({ user, onPhotoClick }) {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [activityFilter, setActivityFilter] = useState("all"); // "all", "inside", "visitors", "staff"

    const residentFlat = user?.tower && user?.flat_no ? `${user.tower}-${user.flat_no}` : user?.flat_no || "Your Flat";

    const loadActivity = async (showToast = false) => {
        if (showToast) setIsRefreshing(true);
        try {
            const res = await api.get("/resident/recent-activity");
            setLogs(res.data || []);
            if (showToast) toast.success("Gate activity refreshed");
        } catch (err) {
            if (showToast) toast.error("Could not refresh activity");
        } finally {
            setLoading(false);
            if (showToast) setIsRefreshing(false);
        }
    };

    useEffect(() => {
        loadActivity();
        const interval = setInterval(() => {
            if (typeof document !== "undefined" && document.visibilityState === "visible") {
                loadActivity(false);
            }
        }, 20000);
        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, [user?.tower, user?.flat_no]);

    const insideCount = useMemo(() => logs.filter((v) => v.status === "entered").length, [logs]);
    const visitorCount = useMemo(() => logs.filter((v) => !v._isDailyHelp).length, [logs]);
    const staffCount = useMemo(() => logs.filter((v) => Boolean(v._isDailyHelp)).length, [logs]);

    const filtered = useMemo(() => {
        return logs.filter((v) => {
            if (activityFilter === "inside") return v.status === "entered";
            if (activityFilter === "visitors") return !v._isDailyHelp;
            if (activityFilter === "staff") return Boolean(v._isDailyHelp);
            return true;
        });
    }, [logs, activityFilter]);

    return (
        <div className="nest-card p-6 sm:p-7 flex flex-col" data-testid="resident-recent-activity-panel">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="flex items-center gap-2">
                        <p className="nest-stat-label">Today at the gate</p>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-nest-alt text-nest-ink3 border border-nest-line">
                            Flat {residentFlat}
                        </span>
                    </div>
                    <h3 className="font-display text-2xl sm:text-3xl mt-1 tracking-tight flex items-center gap-2.5">
                        <span>Recent activity</span>
                        <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-nest-alt text-nest-ink font-sans">
                            {logs.length}
                        </span>
                    </h3>
                </div>

                <button
                    type="button"
                    onClick={() => loadActivity(true)}
                    disabled={isRefreshing}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-nest-ink2 hover:text-nest-ink p-2 rounded-xl hover:bg-nest-alt transition cursor-pointer active:scale-95"
                    title="Refresh activity list"
                    data-testid="refresh-gate-activity-btn"
                >
                    <RefreshCw size={14} className={isRefreshing ? "animate-spin text-nest-clay" : ""} />
                    <span>{isRefreshing ? "Syncing…" : "Refresh"}</span>
                </button>
            </div>

            {/* Filter Pills */}
            <div className="flex items-center gap-1.5 mt-4 overflow-x-auto pb-1 text-xs">
                <button
                    type="button"
                    onClick={() => setActivityFilter("all")}
                    className={`px-3 py-1 rounded-lg font-medium transition cursor-pointer whitespace-nowrap ${
                        activityFilter === "all" ? "bg-nest-ink text-white" : "bg-nest-alt hover:bg-nest-line/60 text-nest-ink2"
                    }`}
                >
                    All ({logs.length})
                </button>
                <button
                    type="button"
                    onClick={() => setActivityFilter("inside")}
                    className={`px-3 py-1 rounded-lg font-medium transition cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                        activityFilter === "inside" ? "bg-nest-sage text-white font-semibold" : "bg-emerald-50 hover:bg-emerald-100 text-emerald-800"
                    }`}
                >
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Inside ({insideCount})
                </button>
                <button
                    type="button"
                    onClick={() => setActivityFilter("visitors")}
                    className={`px-3 py-1 rounded-lg font-medium transition cursor-pointer whitespace-nowrap ${
                        activityFilter === "visitors" ? "bg-nest-ink text-white" : "bg-nest-alt hover:bg-nest-line/60 text-nest-ink2"
                    }`}
                >
                    Visitors ({visitorCount})
                </button>
                <button
                    type="button"
                    onClick={() => setActivityFilter("staff")}
                    className={`px-3 py-1 rounded-lg font-medium transition cursor-pointer whitespace-nowrap ${
                        activityFilter === "staff" ? "bg-nest-ink text-white" : "bg-nest-alt hover:bg-nest-line/60 text-nest-ink2"
                    }`}
                >
                    Daily Staff ({staffCount})
                </button>
            </div>

            {/* Activity List */}
            <div className="mt-4 divide-y divide-nest-line overflow-y-auto max-h-[520px] pr-1">
                {loading && logs.length === 0 ? (
                    <div className="py-12 text-center text-nest-ink3 text-sm">Loading gate activity…</div>
                ) : filtered.length === 0 ? (
                    <div className="py-12 text-center text-nest-ink3 space-y-1.5" data-testid="resident-recent-activity-empty">
                        <div className="h-10 w-10 rounded-xl bg-nest-alt/60 grid place-items-center mx-auto text-nest-ink3 mb-2">
                            <ShieldCheck size={20} />
                        </div>
                        <p className="text-sm font-medium text-nest-ink">No gate activity for Flat {residentFlat} today.</p>
                        <p className="text-xs text-nest-ink3 max-w-sm mx-auto">
                            When visitors, deliveries, or staff assigned to flat {residentFlat} check in at the gate, their entries will show up here.
                        </p>
                    </div>
                ) : (
                    filtered.map((item) => {
                        const hasPhoto = Boolean(item._isWalkin || item.has_photo || item.photo_path || item.photo_url);
                        const activityText = formatActivityTime(item);

                        return (
                            <div
                                key={item.log_id || item.visitor_id || item.help_id || item.code}
                                className="py-3.5 flex items-center justify-between gap-3 transition hover:bg-nest-alt/40 -mx-2 px-2 rounded-xl"
                                data-testid={`resident-activity-item-${item.help_id || item.visitor_id || item.log_id}`}
                            >
                                <div className="flex items-center gap-3 min-w-0 pr-1 flex-1">
                                    {/* Photo Thumbnail */}
                                    {hasPhoto ? (
                                        <button
                                            type="button"
                                            onClick={() => onPhotoClick && onPhotoClick(item)}
                                            className="h-11 w-11 rounded-xl overflow-hidden border border-nest-line flex-shrink-0 bg-nest-alt hover:ring-2 hover:ring-nest-clay transition cursor-pointer relative group"
                                            title="Click to view full photo"
                                            data-testid={`activity-photo-${item.help_id || item.visitor_id}`}
                                        >
                                            <img src={getVisitorPhotoSrc(item)} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                                        </button>
                                    ) : (
                                        <div className="h-11 w-11 rounded-xl border border-nest-line bg-nest-alt/60 flex-shrink-0 grid place-items-center text-nest-ink3 font-display font-semibold">
                                            {item.name?.[0] || <User size={16} />}
                                        </div>
                                    )}

                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <p className="font-semibold text-sm truncate text-nest-ink">{item.name}</p>
                                            {item.service_type && (!item._isWalkin || item.service_type.toLowerCase() !== "walk-in") && (
                                                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">
                                                    {item.service_type}
                                                </span>
                                            )}
                                            {item._isWalkin && (
                                                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-nest-clay/15 text-nest-clay">
                                                    Walk-in
                                                </span>
                                            )}
                                            {item.vehicle_no && (
                                                <span className="inline-flex items-center gap-1 font-mono text-[10px] bg-nest-alt px-1.5 py-0.5 rounded border border-nest-line text-nest-ink" title="Vehicle">
                                                    {item.vehicle_type === "bike" ? <Bike size={11} className="text-nest-clay" /> : <Car size={11} className="text-nest-clay" />}
                                                    <span>{item.vehicle_no}</span>
                                                </span>
                                            )}
                                        </div>

                                        {(() => {
                                            const hostFlat = item.host_flat || residentFlat;
                                            let cleanPurpose = (item.purpose || (item._isDailyHelp ? "Staff" : "Visitor")).trim();
                                            if (hostFlat) {
                                                cleanPurpose = cleanPurpose.replace(new RegExp(`\\s*·\\s*${hostFlat.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')}$`, "i"), "").trim();
                                            }
                                            return (
                                                <p className="text-xs text-nest-ink3 truncate mt-0.5">
                                                    <span>{cleanPurpose}</span>
                                                    {hostFlat && cleanPurpose.toLowerCase() !== hostFlat.toLowerCase() && (
                                                        <> · <span className="font-medium text-nest-ink2">{hostFlat}</span></>
                                                    )}
                                                </p>
                                            );
                                        })()}

                                        {activityText && (
                                            <p className="text-[11px] text-nest-clay font-medium mt-0.5 flex items-center gap-1">
                                                <Clock size={11} />
                                                <span>{activityText}</span>
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Status Badge */}
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <span
                                        className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded ${
                                            item.status === "entered"
                                                ? "bg-nest-sage text-white"
                                                : item.status === "exited"
                                                ? "bg-nest-sand text-nest-ink"
                                                : item.status === "approved"
                                                ? "bg-emerald-600 text-white"
                                                : item.status === "rejected"
                                                ? "bg-nest-err text-white"
                                                : "bg-nest-warn text-white"
                                        }`}
                                    >
                                        {item.status}
                                    </span>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
